/**
 * Licencias (sección 6): gestión admin de las licencias/suscripciones REALES
 * (M10). Lista con filtros (estado de licencia y de suscripción, por vencer) +
 * paginación; alerta visual para las que vencen en <7 días. Enlazable desde
 * Usuarios con ?terapeuta=<id>. La activación sigue siendo webhook-driven; el
 * detalle permite suspender / reactivar (auditado). No hay activación manual.
 */
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useQuery } from "../../hooks/useQuery.js";
import type { EstadoLicencia, EstadoSuscripcion } from "../../api/types.js";
import { Async, fmtFechaCorta } from "./ui.js";
import s from "./admin.module.css";

const LIMIT = 50;
const ESTADOS_SUB: EstadoSuscripcion[] = [
  "pendiente_pago",
  "activa",
  "morosa",
  "cancelada",
  "expirada",
];

export function LicenciaBadge({ estado }: { estado: EstadoLicencia }) {
  return estado === "activa" ? (
    <span className={`${s.badge} ${s.badgeGreen}`}>Activa</span>
  ) : (
    <span className={`${s.badge} ${s.badgeNeutral}`}>Inactiva</span>
  );
}

const SUB_COLOR: Record<EstadoSuscripcion, string> = {
  activa: "badgeGreen",
  morosa: "badgeAmber",
  pendiente_pago: "badgeBlue",
  cancelada: "badgeNeutral",
  expirada: "badgeRed",
};

export function SubBadge({ estado }: { estado: EstadoSuscripcion }) {
  const cls = (s as Record<string, string>)[SUB_COLOR[estado]] ?? s.badgeNeutral;
  return <span className={`${s.badge} ${cls}`}>{estado.replace("_", " ")}</span>;
}

export function AdminLicencias() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const terapeutaId = params.get("terapeuta") ?? "";

  const [estadoLic, setEstadoLic] = useState<"" | EstadoLicencia>("");
  const [estadoSub, setEstadoSub] = useState<"" | EstadoSuscripcion>("");
  const [porVencer, setPorVencer] = useState(false);
  const [offset, setOffset] = useState(0);

  const q = useQuery(
    () =>
      adminApi.licencias({
        ...(estadoLic ? { estadoLicencia: estadoLic } : {}),
        ...(estadoSub ? { estadoSuscripcion: estadoSub } : {}),
        ...(terapeutaId ? { terapeutaId } : {}),
        ...(porVencer ? { porVencer: true } : {}),
        limit: LIMIT,
        offset,
      }),
    [estadoLic, estadoSub, terapeutaId, porVencer, offset],
  );

  function cambiarFiltro(fn: () => void) {
    setOffset(0);
    fn();
  }

  const total = q.data?.total ?? 0;

  return (
    <>
      <div className={s.toolbar} style={{ marginBottom: 4 }}>
        <div className={s.sectionTitle}>Créditos y ventas</div>
        <span className={s.headerSpacer} />
        <button className={s.btn} onClick={() => navigate("/admin/creditos/paquetes")}>
          Paquetes
        </button>
        <button className={s.btn} onClick={() => navigate("/admin/creditos/pagos-offline")}>
          Pagos offline
        </button>
        <button className={s.btn} onClick={() => navigate("/admin/creditos/asignaciones")}>
          Asignaciones
        </button>
      </div>

      {terapeutaId ? (
        <div className={`${s.banner} ${s.bannerInfo}`}>
          Filtrando por un terapeuta.{" "}
          <button
            className={s.btn}
            onClick={() => {
              params.delete("terapeuta");
              setParams(params, { replace: true });
              setOffset(0);
            }}
          >
            Quitar filtro
          </button>
        </div>
      ) : null}

      <div className={s.toolbar}>
        <select
          className={s.select}
          value={estadoLic}
          onChange={(e) => cambiarFiltro(() => setEstadoLic(e.target.value as "" | EstadoLicencia))}
        >
          <option value="">Toda licencia</option>
          <option value="activa">Activa</option>
          <option value="inactiva">Inactiva</option>
        </select>
        <select
          className={s.select}
          value={estadoSub}
          onChange={(e) => cambiarFiltro(() => setEstadoSub(e.target.value as "" | EstadoSuscripcion))}
        >
          <option value="">Toda suscripción</option>
          {ESTADOS_SUB.map((e) => (
            <option key={e} value={e}>
              {e.replace("_", " ")}
            </option>
          ))}
        </select>
        <label className={s.count} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={porVencer}
            onChange={(e) => cambiarFiltro(() => setPorVencer(e.target.checked))}
          />
          Por vencer (&lt;7 días)
        </label>
        <span className={s.count}>{total} suscripciones legacy</span>
      </div>

      <div className={`${s.banner} ${s.bannerInfo}`}>
        Suscripciones/licencias <strong>legacy</strong> (histórico). El modelo actual es por
        <strong> créditos</strong>: ventas en Paquetes / Pagos offline / Asignaciones.
      </div>

      <div className={s.panel}>
        <Async q={q}>
          {(data) => (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Terapeuta</th>
                    <th>Producto</th>
                    <th>Licencia</th>
                    <th>Suscripción</th>
                    <th>Vence</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((l) => (
                    <tr
                      key={l.licenseId}
                      className={s.rowClickable}
                      onClick={() => navigate(`/admin/creditos/${l.licenseId}`)}
                    >
                      <td>
                        {l.terapeutaNombre ?? <span className={s.mono}>{l.terapeutaId.slice(0, 8)}</span>}
                        {l.terapeutaEmail ? <div className={s.muted}>{l.terapeutaEmail}</div> : null}
                      </td>
                      <td className={s.muted}>{l.productoNombre ?? l.productoCodigo ?? "—"}</td>
                      <td>
                        <LicenciaBadge estado={l.estadoLicencia} />
                      </td>
                      <td>
                        <SubBadge estado={l.estadoSuscripcion} />
                      </td>
                      <td className={s.muted}>
                        {fmtFechaCorta(l.currentPeriodEnd)}
                        {l.porVencer ? (
                          <span className={`${s.badge} ${s.badgeAmber}`} style={{ marginLeft: 6 }}>
                            por vencer
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={s.empty}>
                        Sin licencias con esos filtros.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </div>

      <div className={s.toolbar} style={{ marginTop: 12, justifyContent: "flex-end" }}>
        <button className={s.btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
          ‹ Anteriores
        </button>
        <span className={s.count}>
          {total === 0 ? 0 : offset + 1}–{Math.min(offset + LIMIT, total)} de {total}
        </span>
        <button className={s.btn} disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>
          Siguientes ›
        </button>
      </div>
    </>
  );
}
